import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ProdutosLista } from "@/components/ProdutosLista";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ChatServico, fmtDataBR } from "@/components/ChatServico";
import { RetomadaDatas } from "@/components/RetomadaDatas";
import { prepararFoto } from "@/lib/imagem";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const fileUrl = (ev) => `${API.replace("/api", "")}/api/files/${ev.servico_id}/${ev.categoria}/${(ev.path || "").split("/").pop()}?s=${ev.sig || ""}`;

export default function MontadorServico() {
  const { servicoId } = useParams();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [datas, setDatas] = useState(["", "", ""]);
  const [checklist, setChecklist] = useState({});
  const [manualSt, setManualSt] = useState(null);
  const [pend, setPend] = useState(null);
  const [etapaSel, setEtapaSel] = useState(null);
  const [centralAberto, setCentralAberto] = useState(false);
  const [naoLidas, setNaoLidas] = useState({});

  const loadNaoLidas = () => {
    api.get("/chat/nao-lidas", { params: { ids: servicoId } })
      .then(r => setNaoLidas(r.data[servicoId] || {})).catch(() => {});
  };
  useEffect(() => {
    loadNaoLidas();
    const t = setInterval(loadNaoLidas, 20000);
    return () => clearInterval(t);
  }, [servicoId]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = () => Promise.all([
    api.get(`/servicos/${servicoId}`),
    api.get(`/servicos/${servicoId}/manual-status`).catch(() => ({ data: null })),
    api.get(`/servicos/${servicoId}/pendencias-execucao`).catch(() => ({ data: null })),
  ]).then(([r1, r2, r3]) => {
    setS(r1.data); setChecklist(r1.data.checklist_antes || {});
    setManualSt(r2.data); setPend(r3.data);
  });
  useEffect(() => { load(); }, [servicoId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!s) return <div className="p-12 text-muted-foreground">Carregando…</div>;

  // Fluxo sequencial da execução: cada etapa só abre com a anterior completa
  const pk = new Set((pend?.pendencias || []).map((p) => p.chave));
  // Manual aparece se a pendência central exigir (inclui OS legadas pós-checkout) ou se já foi enviado
  const manualNecessario = pk.has("manual") || !!(manualSt && (manualSt.precisa_manual || manualSt.manual_enviado));
  const etapas = [
    { id: "antes", rotulo: "Antes", ok: pend ? !pk.has("checklist_antes") && !pk.has("fotos_antes") : false },
    ...(manualNecessario ? [{ id: "manual", rotulo: "Manual", ok: pend ? !pk.has("manual") : false }] : []),
    { id: "depois", rotulo: "Depois + Vídeo", ok: pend ? !pk.has("checklist_depois") && !pk.has("fotos_execucao") && !pk.has("video") : false },
    { id: "assinaturas", rotulo: "Assinaturas", ok: pend ? !pk.has("assinaturas") : false },
    { id: "checkout", rotulo: "Check-out", ok: !!s.checkout },
  ];
  const podeAbrir = (id) => {
    const i = etapas.findIndex((e) => e.id === id);
    return i >= 0 && etapas.slice(0, i).every((e) => e.ok);
  };
  // Retorno à pendência: abre automaticamente a PRIMEIRA etapa incompleta (mesmo após check-out)
  const primeiraPendente = (etapas.find((e) => !e.ok) || etapas[etapas.length - 1]).id;
  const etapaAtual = etapaSel && podeAbrir(etapaSel) ? etapaSel : primeiraPendente;

  const enviarDatas = async () => {
    if (datas.some(d => !d)) { toast.error("Informe as 3 datas"); return; }
    try {
      await api.post(`/servicos/${servicoId}/datas`, { datas });
      toast.success("Datas enviadas ao cliente");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const postCheckin = async (fd, msg) => {
    try {
      await api.post(`/servicos/${servicoId}/checkin`, fd);
      toast.success(msg);
    } catch (e) { toast.error(formatApiError(e)); }
    load();
  };

  const checkin = async () => {
    const fd = new FormData();
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((pos) => {
        fd.append("lat", pos.coords.latitude);
        fd.append("lng", pos.coords.longitude);
        postCheckin(fd, "Check-in feito");
      }, () => postCheckin(fd, "Check-in feito (sem GPS)"));
    } else {
      postCheckin(fd, "Check-in feito");
    }
  };

  const minDate = () => { const d = new Date(); d.setDate(d.getDate() + 3); return d.toISOString().split("T")[0]; };

  return (
    <>
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        <div className="border border-border bg-white p-5">
          <div className="flex justify-between mb-2">
            <div className="flex items-center gap-2">
              {s.urgente_juridico && <span className="urgent-pulse w-2 h-2 rounded-full bg-[#E60000]" />}
              <span className="px-2 py-0.5 bg-secondary text-[10px] uppercase tracking-wider">{s.tipo}</span>
              <span className="text-xs label-cap">{s.status}</span>
            </div>
          </div>
          <div className="font-heading font-bold text-xl">{s.cliente_nome}</div>
          {s.status === "finalizado" && (
            <button
              onClick={async () => {
                try {
                  const r = await api.get(`/servicos/${servicoId}/relatorio-final.pdf`, { responseType: "blob" });
                  const url = URL.createObjectURL(r.data);
                  const a = document.createElement("a");
                  a.href = url; a.download = `relatorio_final_${s.pim_code || servicoId}.pdf`;
                  a.click(); URL.revokeObjectURL(url);
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-[#0A2540] border border-[#0A2540]/30 rounded px-2 py-1 hover:bg-slate-50"
              data-testid="btn-relatorio-final-montador">
              📄 Relatório Final (PDF)
            </button>
          )}
          <button
            onClick={() => { setCentralAberto(true); }}
            className="relative mt-1 inline-flex items-center gap-1 text-xs font-bold text-[#B45309] bg-[#FFF7E0] border border-[#F5C518] rounded px-2 py-1 hover:bg-[#fdeeba]"
            data-testid="falar-central-btn">
            📞 Falar com a central sobre este cliente
            {naoLidas.interno > 0 && (
              <span className="min-w-[16px] h-4 px-1 rounded-full bg-[#E60000] text-white text-[10px] font-bold flex items-center justify-center"
                data-testid="central-nao-lidas">{naoLidas.interno > 9 ? "9+" : naoLidas.interno}</span>
            )}
          </button>
          <Dialog open={centralAberto} onOpenChange={(o) => { setCentralAberto(o); if (!o) loadNaoLidas(); }}>
            <DialogContent className="max-w-2xl" data-testid="central-chat-dialog">
              <DialogHeader><DialogTitle className="text-base">Falar com a central — {s.cliente_nome}</DialogTitle></DialogHeader>
              <div className="text-[11px] text-muted-foreground -mt-2">
                🔒 Canal interno com a central (montadora/empresa). O cliente <strong>nunca</strong> vê estas mensagens.
              </div>
              <ChatServico meuTipo="montador" titulo="Canal interno (você × central)"
                listar={async () => (await api.get(`/servicos/${s.id}/chat`, { params: { canal: "interno" } })).data}
                enviar={async (t) => api.post(`/servicos/${s.id}/chat`, { texto: t, canal: "interno" })} />
              <button
                onClick={async () => {
                  const texto = window.prompt("Descreva a dúvida URGENTE: a tela da central ficará bloqueada até responder.");
                  if (!texto || !texto.trim()) return;
                  try {
                    await api.post(`/servicos/${s.id}/central-pendencia`, { texto: texto.trim() });
                    toast.success("Pendência urgente aberta! A central será bloqueada até responder você.");
                  } catch (e2) { toast.error(formatApiError(e2)); }
                }}
                className="text-xs font-bold text-[#E60000] underline text-left"
                data-testid="abrir-pendencia-urgente-btn">
                🚨 É urgente e a central não responde? Abrir pendência que bloqueia a tela da central
              </button>
            </DialogContent>
          </Dialog>
          <div className="text-sm text-muted-foreground mt-1">
            {s.cliente_endereco}{s.cliente_numero ? `, ${s.cliente_numero}` : ""}{s.cliente_bairro ? ` — ${s.cliente_bairro}` : ""}{s.cliente_cidade ? ` · ${s.cliente_cidade}/${s.cliente_estado || ""}` : ""}
          </div>
          {(s.cliente_cep || s.cliente_complemento || s.cliente_referencia) && (
            <div className="text-xs text-muted-foreground mt-0.5" data-testid="cliente-endereco-extra">
              {s.cliente_cep ? `CEP ${s.cliente_cep}` : ""}
              {s.cliente_complemento ? ` · ${s.cliente_complemento}` : ""}
              {s.cliente_referencia ? ` · Ref: ${s.cliente_referencia}` : ""}
            </div>
          )}
          <div className="text-sm mt-1 text-muted-foreground" data-testid="chat-aviso">
            Comunicação com o cliente: use o <strong>chat registrado</strong> abaixo.
          </div>
          <div className="mt-3 pt-3 border-t border-border text-sm">
            <strong>{s.produtos?.length > 1 ? `Pedido com ${s.produtos.length} itens` : s.produto}</strong> · Qtd {s.quantidade} {s.cor && ` · ${s.cor}`}
            <ProdutosLista produtos={s.produtos} />
          </div>
          <div className="text-xs text-muted-foreground mt-1" data-testid="servico-os">
            OS: {s.os_numero || s.pim_code}
          </div>
          <div className="mt-2 text-sm" data-testid="valor-montador">
            {s.valor_montador ? (
              <span className="font-bold text-[#00A859]">Você recebe: R$ {Number(s.valor_montador).toFixed(2)}</span>
            ) : (
              <span className="text-muted-foreground">
                Valor: a empresa ainda não cadastrou a tabela de valores — consulte "Valores das Empresas".
              </span>
            )}
          </div>
        </div>

        <ChatServico meuTipo="montador" titulo="Chat com o cliente (canal externo — o cliente vê)"
          somenteLeitura={["finalizado", "cancelado", "DEMO_ENCERRADA"].includes(s?.status)}
          listar={async () => (await api.get(`/servicos/${s.id}/chat`, { params: { canal: "externo" } })).data}
          enviar={async (t) => api.post(`/servicos/${s.id}/chat`, { texto: t, canal: "externo" })} />

        {/* Datas: cliente informou disponibilidade OU técnico escolhe D+1..D+3 */}
        {s.status === "aguardando_data_tecnico" && (
          <EscolherData servicoId={s.id} datasCliente={s.datas_cliente || []} onSaved={load} />
        )}

        {/* Aguardando cliente */}
        {s.status === "aguardando_confirmacao_cliente" && (
          <div className="border border-border bg-white p-5">
            <div className="label-cap">Aguardando confirmação do cliente</div>
            <div className="mt-2 text-sm">
              <div>Datas propostas:</div>
              {s.datas_propostas.map((d) => <div key={d} className="capitalize">{fmtDataBR(d)}</div>)}
            </div>
          </div>
        )}

        {(s.status === "ausente" || s.status === "impedido" ||
          (s.status === "agendado" && (s.data_confirmada || "") <
            new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
              .toISOString().slice(0, 10))) && (
          <RetomadaDatas servicoId={servicoId} s={s} onSaved={load} />
        )}

        {/* Agendado: check-in */}
        {s.status === "agendado" && (
          <div className="border-2 border-[#00A859] bg-white p-5">
            <div className="label-cap mb-2">Agendado para</div>
            <div className="font-heading font-bold text-2xl capitalize">{fmtDataBR(s.data_confirmada)}</div>
            <Button onClick={checkin} className="mt-4 w-full btn-sharp brand-bg text-white h-14 text-lg" data-testid="checkin-btn">
              Fazer Check-in
            </Button>
          </div>
        )}

        {/* Em atendimento — fluxo SEQUENCIAL: Antes → Manual → Depois/Vídeo → Assinaturas → Check-out */}
        {s.status === "em_atendimento" && pend && (
          <div className="space-y-4" data-testid="fluxo-execucao">
            <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${etapas.length}, 1fr)` }}>
              {etapas.map((e, i) => {
                const liberada = podeAbrir(e.id);
                const ativa = etapaAtual === e.id;
                return (
                  <button key={e.id} onClick={() => liberada && setEtapaSel(e.id)} disabled={!liberada}
                    className={`btn-sharp px-1 py-2 text-[11px] font-bold border text-center leading-tight
                      ${ativa ? "brand-bg text-white border-transparent"
                        : e.ok ? "bg-[#E8F8EF] text-[#00784A] border-[#00A859]"
                        : liberada ? "bg-white border-border hover:bg-secondary"
                        : "bg-secondary text-muted-foreground border-border opacity-50 cursor-not-allowed"}`}
                    title={liberada ? "" : "Complete a etapa anterior para liberar"}
                    data-testid={`etapa-${e.id}`}>
                    {e.ok ? "✓ " : `${i + 1}. `}{e.rotulo}{!liberada && " 🔒"}
                  </button>
                );
              })}
            </div>
            {etapaAtual === "antes" && <ChecklistAntes servicoId={s.id} s={s} onSaved={load} />}
            {etapaAtual === "manual" && <ManualProduto servicoId={s.id} s={s} onChanged={load} obrigatorio={pk.has("manual")} />}
            {etapaAtual === "depois" && <ChecklistDepois servicoId={s.id} s={s} onSaved={load} />}
            {etapaAtual === "assinaturas" && <Assinaturas servicoId={s.id} s={s} onFinalizado={load} />}
            {etapaAtual === "checkout" && <CheckoutEncerramento servicoId={s.id} s={s} pend={pend} onDone={load} />}
          </div>
        )}

        {/* Reportar ocorrência no local */}
        {["agendado", "em_atendimento"].includes(s.status) && (
          <ReportarOcorrencia servicoId={s.id} onSaved={load} />
        )}

        {/* Evidências já enviadas */}
        {(s.evidencias || []).length > 0 && (
          <div className="border border-border bg-white p-5">
            <div className="label-cap mb-3">Evidências enviadas ({s.evidencias.length})</div>
            <div className="grid grid-cols-3 gap-2">
              {s.evidencias.map((ev) => (
                <div key={ev.id} className="border border-border p-1 text-[10px] min-w-0">
                  <div className="label-cap text-[9px] truncate" title={ev.categoria}>{ev.categoria}</div>
                  {ev.content_type?.startsWith("image/") ? (
                    <img src={fileUrl(ev)} alt={ev.filename} className="mt-1 aspect-square object-cover" />
                  ) : ev.content_type?.startsWith("video/") ? (
                    <video src={fileUrl(ev)} controls playsInline className="mt-1 aspect-square object-cover bg-black"
                      data-testid={`evidencia-video-${ev.id}`} />
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function ChecklistAntes({ servicoId, s, onSaved }) {
  const [form, setForm] = useState({
    nome_cliente_confirmado: false, produto_confirmado: false, cor_confirmada: false,
    quantidade_volumes: 1, tem_tampo: false, tem_espelho_vidro: false,
    ...(s.checklist_antes || {}), fase: "antes",
  });
  const evAntes = (s.evidencias || []).filter((e) => e.categoria === "pre_montagem" && e.content_type?.startsWith("image/"));

  const upload = async (file, categoria) => {
    try {
      const foto = await prepararFoto(file);
      const fd = new FormData();
      fd.append("file", foto);
      fd.append("servico_id", servicoId);
      fd.append("categoria", categoria);
      await api.post("/upload", fd); toast.success("Foto enviada"); onSaved();
    } catch (e) { toast.error(e?.userMessage || formatApiError(e)); }
  };

  const salvar = async () => {
    try {
      await api.post(`/servicos/${servicoId}/checklist`, form);
      toast.success("Checklist salvo");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="border border-border bg-white p-5 space-y-4">
      <div className="label-cap flex items-center justify-between">
        <span>Antes da montagem</span>
        {s.checklist_antes && <span className="text-[10px] font-bold text-[#00784A] bg-[#E8F8EF] px-2 py-0.5" data-testid="antes-salvo">✓ salvo</span>}
      </div>
      <Check label="Nome do cliente confirmado" v={form.nome_cliente_confirmado} on={(v) => setForm(f => ({...f, nome_cliente_confirmado: v}))} testid="ck-nome" />
      <Check label="Me identifiquei ao cliente (identidade apresentada)" v={form.identidade_apresentada} on={(v) => setForm(f => ({...f, identidade_apresentada: v}))} testid="ck-identidade" />
      <Check label="Manual de montagem disponível" v={form.manual_disponivel} on={(v) => setForm(f => ({...f, manual_disponivel: v}))} testid="ck-manual" />
      <Check label="Produto confirmado" v={form.produto_confirmado} on={(v) => setForm(f => ({...f, produto_confirmado: v}))} testid="ck-prod" />
      <Check label="Cor confirmada" v={form.cor_confirmada} on={(v) => setForm(f => ({...f, cor_confirmada: v}))} testid="ck-cor" />
      <div>
        <Label className="label-cap">Quantidade de volumes</Label>
        <Input type="number" value={form.quantidade_volumes} onChange={(e) => setForm(f => ({...f, quantidade_volumes: Number(e.target.value)}))} className="mt-2 w-32" data-testid="ck-vols" />
      </div>
      <Check label="Tem tampo (precisa foto)" v={form.tem_tampo} on={(v) => setForm(f => ({...f, tem_tampo: v}))} testid="ck-tampo" />
      <Check label="Tem espelho/vidro (precisa foto)" v={form.tem_espelho_vidro} on={(v) => setForm(f => ({...f, tem_espelho_vidro: v}))} testid="ck-espelho" />

      <div className="border-t border-border pt-4 space-y-3">
        <FileSlot label="Foto da embalagem" categoria="pre_montagem" testid="up-embalagem" onUpload={upload} ev={evAntes[0]} />
        <FileSlot label="Foto etiqueta dos volumes" categoria="pre_montagem" testid="up-etiqueta" onUpload={upload} ev={evAntes[1]} />
        <FileSlot label="Foto do ambiente" categoria="pre_montagem" testid="up-ambiente" onUpload={upload} ev={evAntes[2]} />
        {form.tem_tampo && <FileSlot label="Foto do tampo" categoria="pre_montagem" testid="up-tampo" onUpload={upload} ev={evAntes[3]} />}
        {form.tem_espelho_vidro && <FileSlot label="Foto do espelho/vidro" categoria="pre_montagem" testid="up-espelho" onUpload={upload} ev={evAntes[4]} />}
      </div>

      <Button onClick={salvar} className="w-full btn-sharp brand-bg text-white" data-testid="ck-salvar-antes">Salvar checklist</Button>
      {evAntes.length === 0 && (
        <div className="text-[11px] text-[#B45309]" data-testid="antes-pendencia-foto">
          Para salvar, é obrigatório enviar ao menos 1 foto do produto/ambiente.
        </div>
      )}
    </div>
  );
}

function ReportarOcorrencia({ servicoId, onSaved }) {
  const TIPOS_OC = [
    ["cliente_ausente", "Cliente ausente"],
    ["menor_de_idade", "Menor de 18 anos na residência"],
    ["produto_nao_entregue", "Produto não entregue"],
    ["assistencia_tecnica", "Produto com assistência técnica"],
    ["aguardar_ordem_montagem", "Aguardar ordem de montagem"],
    ["endereco_nao_encontrado", "Endereço não encontrado"],
    ["nao_deu_montagem", "Produto não deu montagem"],
    ["peca_faltante", "Peça faltante"],
    ["peca_errada", "Peça errada"],
    ["peca_avariada", "Peça quebrada/riscada/avariada na entrega"],
    ["ferragem_incorreta", "Ferragem incorreta / furação incompatível"],
    ["dano_execucao", "Dano durante manuseio/execução"],
    ["exige_ancoragem", "Manual exige ancoragem (fora do escopo)"],
    ["ambiente_inseguro", "Ambiente inseguro"],
    ["fora_do_escopo", "Cliente pediu atividade fora do escopo"],
    ["outro", "Outro"],
  ];
  const [aberto, setAberto] = useState(false);
  const [tipo, setTipo] = useState("");
  const [motivo, setMotivo] = useState("");
  const [fotos, setFotos] = useState([]);
  const [etapa, setEtapa] = useState("form");
  const [enviando, setEnviando] = useState(false);
  const assinRef = useRef(null);
  const [assinou, setAssinou] = useState(false);

  useEffect(() => {
    if (etapa !== "resumo" || !assinRef.current) return;
    const c = assinRef.current;
    const ctx = c.getContext("2d"); ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.strokeStyle = "#142766";
    let drawing = false;
    const pos = (e) => {
      const r = c.getBoundingClientRect();
      const sx = c.width / r.width, sy = c.height / r.height;
      return { x: ((e.touches?.[0]?.clientX ?? e.clientX) - r.left) * sx,
               y: ((e.touches?.[0]?.clientY ?? e.clientY) - r.top) * sy };
    };
    const start = (e) => { drawing = true; const { x, y } = pos(e); ctx.beginPath(); ctx.moveTo(x, y); e.preventDefault(); };
    const move = (e) => { if (!drawing) return; const { x, y } = pos(e); ctx.lineTo(x, y); ctx.stroke(); setAssinou(true); e.preventDefault(); };
    const end = () => { drawing = false; };
    c.addEventListener("mousedown", start); c.addEventListener("mousemove", move); window.addEventListener("mouseup", end);
    c.addEventListener("touchstart", start, { passive: false }); c.addEventListener("touchmove", move, { passive: false }); window.addEventListener("touchend", end);
  }, [etapa]);

  const podeRegistrar = tipo && motivo.trim().length >= 10 && fotos.length >= 2;
  const ehProduto = ["peca_faltante", "peca_errada", "peca_avariada", "ferragem_incorreta", "assistencia_tecnica", "nao_deu_montagem"].includes(tipo);
  const ehLogistica = ["cliente_ausente", "endereco_nao_encontrado"].includes(tipo);

  const enviar = async () => {
    setEnviando(true);
    try {
      for (const f of fotos) {
        const foto = await prepararFoto(f);
        const fd = new FormData();
        fd.append("file", foto); fd.append("servico_id", servicoId); fd.append("categoria", "ocorrencia");
        await api.post("/upload", fd);
      }
      const fd2 = new FormData();
      fd2.append("tipo", tipo); fd2.append("motivo", motivo);
      if (assinou && assinRef.current) fd2.append("assinatura_cliente", assinRef.current.toDataURL());
      await api.post(`/servicos/${servicoId}/ocorrencia`, fd2);
      toast.success(ehProduto
        ? "Ocorrência registrada — OS marcada como Assistência Técnica (prazo de 30 dias iniciado)."
        : "Ocorrência registrada. A empresa foi avisada por WhatsApp.");
      setAberto(false); setTipo(""); setMotivo(""); setFotos([]); setEtapa("form"); setAssinou(false);
      onSaved();
    } catch (e) { toast.error(e?.userMessage || formatApiError(e)); }
    finally { setEnviando(false); }
  };

  if (!aberto) {
    return (
      <Button variant="outline" onClick={() => setAberto(true)}
        className="w-full btn-sharp border-[#E60000] text-[#E60000] hover:bg-red-50 whitespace-normal h-auto py-2 text-center"
        data-testid="oc-abrir">
        ⚠️ Reportar ocorrência
      </Button>
    );
  }
  if (etapa === "resumo") {
    return (
      <div className="border-2 border-[#E60000] bg-white p-5 space-y-3" data-testid="oc-resumo">
        <div className="label-cap">Confira o laudo antes de registrar</div>
        <div className="text-sm"><strong>{TIPOS_OC.find(([k]) => k === tipo)?.[1]}</strong>
          {ehProduto && <span className="ml-2 px-1.5 py-0.5 bg-[#FFEDD5] text-[#B45309] text-[10px] font-bold uppercase">→ Assistência Técnica (30 dias)</span>}
          {ehLogistica && <span className="ml-2 px-1.5 py-0.5 bg-[#FEF3C7] text-[#92400E] text-[10px] font-bold uppercase">→ Reagendamento por culpa do cliente</span>}
        </div>
        <div className="text-sm bg-secondary p-2 whitespace-pre-wrap" data-testid="oc-resumo-texto">{motivo}</div>
        <div className="flex gap-2 flex-wrap" data-testid="oc-resumo-fotos">
          {fotos.map((f, i) => (
            <img key={i} src={URL.createObjectURL(f)} alt={`foto ${i + 1}`} className="w-20 h-20 object-cover border border-border" />
          ))}
        </div>
        <div>
          <Label className="label-cap">Assinatura do cliente (validação no local — opcional se ausente)</Label>
          <canvas ref={assinRef} width={320} height={110} className="mt-1 border border-dashed border-border bg-[#FAFAFA] touch-none w-full max-w-[320px]" data-testid="oc-assinatura" />
          <button className="text-[11px] underline text-muted-foreground" onClick={() => { const c = assinRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setAssinou(false); }}>Limpar assinatura</button>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="btn-sharp flex-1" onClick={() => setEtapa("form")} data-testid="oc-voltar">Voltar</Button>
          <Button className="btn-sharp flex-1 bg-[#E60000] hover:bg-[#c40000] text-white" disabled={enviando}
            onClick={enviar} data-testid="oc-confirmar">
            {enviando ? "Enviando…" : "Confirmar e registrar"}
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="border-2 border-[#E60000] bg-white p-5 space-y-3" data-testid="oc-form">
      <div className="label-cap">Reportar ocorrência no local</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {TIPOS_OC.map(([k, l]) => (
          <button key={k} onClick={() => setTipo(k)}
            className={`btn-sharp p-2.5 text-left text-sm border ${tipo === k ? "bg-[#E60000] text-white border-transparent" : "border-border hover:bg-secondary"}`}
            data-testid={`oc-tipo-${k}`}>
            {l}
          </button>
        ))}
      </div>
      {tipo && (
        <>
          <div>
            <Label className="label-cap">Descrição detalhada do problema * (mín. 10 caracteres)</Label>
            <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} className="mt-1" rows={3} data-testid="oc-motivo" />
          </div>
          <div>
            <Label className="label-cap">Fotos do laudo * (mínimo 2 — {fotos.length} anexada{fotos.length === 1 ? "" : "s"})</Label>
            <Input type="file" accept="image/*" capture="environment" className="mt-1"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) setFotos(fs => [...fs, f]); e.target.value = ""; }} data-testid="oc-foto" />
            <div className="flex gap-2 mt-2 flex-wrap">
              {fotos.map((f, i) => (
                <div key={i} className="relative">
                  <img src={URL.createObjectURL(f)} alt={`foto ${i + 1}`} className="w-16 h-16 object-cover border border-border" />
                  <button onClick={() => setFotos(fs => fs.filter((_, j) => j !== i))}
                    className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-[#E60000] text-white text-[10px] leading-none rounded-full">×</button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      <div className="flex gap-2">
        <Button variant="outline" className="btn-sharp flex-1" onClick={() => setAberto(false)}>Cancelar</Button>
        <Button className="btn-sharp flex-1 bg-[#E60000] hover:bg-[#c40000] text-white" disabled={!podeRegistrar}
          onClick={() => setEtapa("resumo")} data-testid="oc-enviar">
          Revisar e registrar
        </Button>
      </div>
      {!podeRegistrar && tipo && (
        <div className="text-[11px] text-[#B91C1C]" data-testid="oc-pendencias">
          Para registrar: descrição detalhada (mín. 10 caracteres) e no mínimo 2 fotos.
        </div>
      )}
    </div>
  );
}

function ChecklistDepois({ servicoId, s, onSaved }) {
  const [form, setForm] = useState({ descricao: "", ...(s.checklist_depois || {}), fase: "depois" });
  const evDepois = (s.evidencias || []).filter((e) => e.categoria === "montagem" && e.content_type?.startsWith("image/"));
  const evVideo = (s.evidencias || []).find((e) => e.content_type?.startsWith("video/"));
  const upload = async (file, categoria) => {
    try {
      const foto = await prepararFoto(file);
      const fd = new FormData(); fd.append("file", foto); fd.append("servico_id", servicoId); fd.append("categoria", categoria);
      await api.post("/upload", fd); toast.success("Mídia enviada"); onSaved();
    } catch (e) { toast.error(e?.userMessage || formatApiError(e)); }
  };
  const salvar = async () => {
    try { await api.post(`/servicos/${servicoId}/checklist`, form); toast.success("Checklist salvo"); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="border border-border bg-white p-5 space-y-4">
      <div className="label-cap flex items-center justify-between">
        <span>Depois da montagem</span>
        {s.checklist_depois && <span className="text-[10px] font-bold text-[#00784A] bg-[#E8F8EF] px-2 py-0.5" data-testid="depois-salvo">✓ salvo</span>}
      </div>
      <FileSlot label="Produto inteiro" categoria="montagem" testid="up-inteiro" onUpload={upload} ev={evDepois[0]} />
      <FileSlot label="Lateral" categoria="montagem" testid="up-lateral" onUpload={upload} ev={evDepois[1]} />
      <FileSlot label="Gavetas" categoria="montagem" testid="up-gavetas" onUpload={upload} ev={evDepois[2]} />
      <FileSlot label="Portas" categoria="montagem" testid="up-portas" onUpload={upload} ev={evDepois[3]} />
      <FileSlot label="Ferragens" categoria="montagem" testid="up-ferragens" onUpload={upload} ev={evDepois[4]} />
      {evVideo ? (
        <div className="border border-dashed border-border p-3">
          <div className="text-sm flex flex-wrap items-center gap-2">
            <span className="font-semibold">Vídeo</span><span className="text-[11px] font-bold text-[#00784A]" data-testid="video-enviado">✓ já enviado — não precisa reenviar</span>
          </div>
          <video src={fileUrl(evVideo)} controls playsInline className="mt-2 w-full max-h-56 bg-black" data-testid="video-player" />
        </div>
      ) : (
        <FileSlot label="Vídeo (até 10s)" categoria="video" accept="video/*" testid="up-video" onUpload={upload} />
      )}
      <div>
        <Label className="label-cap">Observações</Label>
        <Textarea value={form.descricao} onChange={(e) => setForm(f => ({...f, descricao: e.target.value}))} className="mt-2" data-testid="ck-desc" />
      </div>
      <Button onClick={salvar} className="w-full btn-sharp brand-bg text-white" data-testid="ck-salvar-depois">Salvar</Button>
      {evDepois.length === 0 && (
        <div className="text-[11px] text-[#B45309]" data-testid="depois-pendencia-foto">
          Para salvar, é obrigatório enviar ao menos 1 foto do produto montado.
        </div>
      )}
    </div>
  );
}

function EscolherData({ servicoId, datasCliente = [], onSaved }) {
  const [datas, setDatas] = useState([]);
  const [escolhida, setEscolhida] = useState("");
  const [loading, setLoading] = useState(false);

  const minDia = new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 10);
  const clienteDatas = [...new Set(datasCliente)].filter((d) => d >= minDia);
  const modoCliente = clienteDatas.length > 0;

  useEffect(() => {
    if (modoCliente) {
      setDatas(clienteDatas.map((d) => ({ data: d, capacidade: null })));
    } else {
      api.get("/montador/proximas-datas").then(r => setDatas(r.data.datas || []));
    }
  }, []);

  const enviar = async () => {
    if (!escolhida) { toast.error("Selecione uma data"); return; }
    setLoading(true);
    try {
      const fd = new FormData(); fd.append("data_escolhida", escolhida);
      let r = await api.post(`/servicos/${servicoId}/escolher-data`, fd);
      if (r.data?.alerta_ultima_data) {
        const seguir = window.confirm(r.data.mensagem);
        if (!seguir) { setLoading(false); return; }
        const fd2 = new FormData();
        fd2.append("data_escolhida", escolhida);
        fd2.append("confirmar_ultima_data", "true");
        r = await api.post(`/servicos/${servicoId}/escolher-data`, fd2);
      }
      toast.success(r.data.status === "agendado" ? "Agendado! O cliente já foi avisado." : "Data enviada ao cliente");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="border-2 border-[#FFCC00] bg-white p-5">
      <div className="label-cap mb-1">
        {modoCliente ? "Datas informadas pelo cliente — escolha 1" : "Escolha 1 data (D+2 a D+4)"}
      </div>
      <div className="text-xs text-muted-foreground mb-3">
        {modoCliente
          ? "O cliente já declarou disponibilidade nestas datas. Ao escolher, o agendamento é confirmado na hora."
          : "Domingos e datas sem capacidade não aparecem."}
      </div>
      <div className="space-y-2">
        {datas.length === 0 && <div className="text-sm text-muted-foreground">Sem disponibilidade. Limite de 10 montagens em 7 dias atingido.</div>}
        {datas.map((d) => (
          <button key={d.data} onClick={() => setEscolhida(d.data)}
            className={`w-full btn-sharp p-3 text-left flex justify-between items-center ${escolhida === d.data ? "brand-bg text-white border-transparent" : "border border-border hover:bg-secondary"}`}
            data-testid={`opt-data-${d.data}`}>
            <div>
              <div className="font-heading font-bold">{new Date(d.data + "T00:00:00").toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })}</div>
              <div className={`text-xs ${escolhida === d.data ? "text-white/80" : "text-muted-foreground"}`}>{d.data}</div>
            </div>
            {d.capacidade
              ? <span className="text-xs font-mono">{d.capacidade.restantes}/{d.capacidade.limite} vagas</span>
              : <span className="text-[10px] uppercase tracking-wider font-bold text-[#00A859]">Cliente disponível</span>}
          </button>
        ))}
      </div>
      <Button onClick={enviar} disabled={!escolhida || loading} className="mt-4 w-full btn-sharp brand-bg text-white" data-testid="enviar-data">
        {loading ? "Enviando…" : modoCliente ? "Confirmar e agendar" : "Confirmar data e enviar ao cliente"}
      </Button>
    </div>
  );
}

function Assinaturas({ servicoId, s, onFinalizado }) {
  const tecRef = useRef(null);
  const cliRef = useRef(null);
  const jaAssinado = !!(s.assinatura_cliente && s.assinatura_tecnico);

  const initCanvas = (ref) => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext("2d"); ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.strokeStyle = "#142766";
    let drawing = false;
    const pos = (e) => {
      const r = c.getBoundingClientRect();
      const sx = c.width / r.width, sy = c.height / r.height;
      return { x: ((e.touches?.[0]?.clientX ?? e.clientX) - r.left) * sx,
               y: ((e.touches?.[0]?.clientY ?? e.clientY) - r.top) * sy };
    };
    const start = (e) => { drawing = true; const {x,y} = pos(e); ctx.beginPath(); ctx.moveTo(x,y); e.preventDefault(); };
    const move = (e) => { if (!drawing) return; const {x,y} = pos(e); ctx.lineTo(x,y); ctx.stroke(); e.preventDefault(); };
    const end = () => { drawing = false; };
    c.addEventListener("mousedown", start); c.addEventListener("mousemove", move); window.addEventListener("mouseup", end);
    c.addEventListener("touchstart", start, { passive: false }); c.addEventListener("touchmove", move, { passive: false }); window.addEventListener("touchend", end);
  };

  useEffect(() => { if (!jaAssinado) { initCanvas(tecRef); initCanvas(cliRef); } }, [jaAssinado]); // eslint-disable-line react-hooks/exhaustive-deps

  const limpar = (ref) => { const c = ref.current; c.getContext("2d").clearRect(0,0,c.width,c.height); };

  const enviarAssinaturas = async () => {
    try {
      await api.post(`/servicos/${servicoId}/assinaturas`, {
        assinatura_tecnico: tecRef.current.toDataURL(),
        assinatura_cliente: cliRef.current.toDataURL(),
      });
      toast.success("Assinaturas registradas");
      onFinalizado();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (jaAssinado) {
    return (
      <div className="border border-border bg-white p-6 space-y-4" data-testid="assinaturas-registradas">
        <div className="text-sm font-bold text-[#00784A]">✓ Assinaturas já registradas — não é preciso refazer.</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-cap">Técnico</Label>
            <img src={s.assinatura_tecnico} alt="Assinatura do técnico" className="mt-1 border border-border bg-[#FAFAFA] w-full" data-testid="assinatura-tec-salva" />
          </div>
          <div>
            <Label className="label-cap">Cliente</Label>
            <img src={s.assinatura_cliente} alt="Assinatura do cliente" className="mt-1 border border-border bg-[#FAFAFA] w-full" data-testid="assinatura-cli-salva" />
          </div>
        </div>
        <div className="text-[11px] text-muted-foreground">Avance para o Check-out na etapa seguinte.</div>
      </div>
    );
  }

  return (
    <div className="border border-border bg-white p-6 space-y-8">
      <div className="max-w-md mx-auto w-full">
        <Label className="label-cap">Assinatura do Técnico</Label>
        <div className="border border-border bg-[#FAFAFA] rounded-md mt-3"><canvas ref={tecRef} width={600} height={220} className="w-full touch-none" data-testid="canvas-tec" /></div>
        <button onClick={() => limpar(tecRef)} className="text-xs border border-border px-3 py-1.5 mt-2 hover:bg-secondary" data-testid="limpar-tec">Limpar</button>
      </div>
      <div className="max-w-md mx-auto w-full">
        <Label className="label-cap">Assinatura do Cliente</Label>
        <div className="border border-border bg-[#FAFAFA] rounded-md mt-3"><canvas ref={cliRef} width={600} height={220} className="w-full touch-none" data-testid="canvas-cli" /></div>
        <button onClick={() => limpar(cliRef)} className="text-xs border border-border px-3 py-1.5 mt-2 hover:bg-secondary" data-testid="limpar-cli">Limpar</button>
      </div>
      <Button onClick={enviarAssinaturas} className="w-full btn-sharp brand-bg text-white" data-testid="enviar-assinaturas">Registrar assinaturas</Button>
    </div>
  );
}

function CheckoutEncerramento({ servicoId, s, pend, onDone }) {
  const pendencias = pend?.pendencias || [];
  const bloqueado = pendencias.length > 0;

  const fazerCheckout = async () => {
    const enviar = async (fd) => {
      try {
        await api.post(`/servicos/${servicoId}/checkout`, fd);
        toast.success("Check-out feito");
        onDone();
      } catch (e) { toast.error(formatApiError(e)); }
    };
    const fd = new FormData();
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(async (pos) => {
        fd.append("lat", pos.coords.latitude);
        fd.append("lng", pos.coords.longitude);
        await enviar(fd);
      }, async () => { await enviar(fd); });
    } else {
      await enviar(fd);
    }
  };

  const encerrar = async (tipo, extra = {}) => {
    try {
      await api.post(`/servicos/${servicoId}/encerrar`, { tipo, ...extra });
      toast.success(`Encerrado: ${tipo}`);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="border border-border bg-white p-6 space-y-5">
      {bloqueado && (
        <div className="text-[12px] text-[#B45309] bg-[#FEF3C7] border border-[#FDE68A] px-3 py-2" data-testid="checkout-pendencias">
          <div className="font-bold mb-1">Check-out bloqueado — pendências obrigatórias:</div>
          {pendencias.map((p) => <div key={p.chave} data-testid={`pendencia-${p.chave}`}>• {p.rotulo}</div>)}
        </div>
      )}
      {s.checkout ? (
        <div className="text-sm font-bold text-[#00784A]" data-testid="checkout-feito">✓ Check-out registrado — falta apenas o encerramento abaixo.</div>
      ) : (
        <Button onClick={fazerCheckout} disabled={bloqueado} variant="outline" className="w-full btn-sharp" data-testid="checkout-btn">
          Check-out (registrar saída)
        </Button>
      )}

      <div className="border-t border-border pt-4">
        <div className="label-cap mb-3">Encerramento (escolha 1)</div>
        <div className="grid grid-cols-2 gap-2">
          <Button onClick={() => encerrar("concluida", { observacao: prompt("Observação livre (piso irregular, sem elevador, etc):") || "" })}
            disabled={bloqueado || !s.checkout} title={bloqueado ? "Resolva as pendências acima para concluir" : !s.checkout ? "Registre o check-out para concluir" : ""}
            className="btn-sharp bg-[#00A859] text-white hover:bg-[#007a42] h-12 text-xs" data-testid="enc-concluida">
            ✓ Concluída
          </Button>
          {["revisao", "assistencia"].includes(s?.tipo) && (
          <Button onClick={() => { const motivo = prompt("Motivo da troca total:"); if (motivo) encerrar("troca_total", { motivo, quantidade_volumes: 1 }); }}
            className="btn-sharp bg-[#E60000] text-white h-12 text-xs" data-testid="enc-troca">
            ⟲ Troca Total
          </Button>
          )}
          <Button onClick={() => { const desc = prompt("Descrição do cancelamento:"); if (desc) encerrar("cancelamento", { descricao: desc }); }}
            className="btn-sharp bg-foreground text-white h-12 text-xs" data-testid="enc-cancelar">
            ✕ Cancelar
          </Button>
        </div>
        <div className="text-[11px] text-foreground/70 mt-2">
          Ao concluir, a OS vai para "Finalizados". Houve problema no produto ou no atendimento? Use "Reportar Ocorrência" (auditoria). Troca Total é decidida somente na revisão/assistência.
        </div>
      </div>
    </div>
  );
}

function Check({ label, v, on, testid }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <Checkbox checked={!!v} onCheckedChange={(c) => on(!!c)} data-testid={testid} />
      <span className="text-sm">{label}</span>
    </label>
  );
}
function ManualProduto({ servicoId, s, onChanged, obrigatorio = false }) {
  const [st, setSt] = useState(null);
  const [folhas, setFolhas] = useState([]);
  const [busy, setBusy] = useState(false);
  const camRef = useRef(null);
  const galRef = useRef(null);
  const load = () => { api.get(`/servicos/${servicoId}/manual-status`).then(r => setSt(r.data)).catch(() => {}); };
  useEffect(load, [servicoId]);
  const jaEnviadas = (s?.evidencias || []).filter((e) => e.categoria === "manual").length;
  const precisa = st ? (st.precisa_manual || st.manual_enviado) : false;
  if (!precisa && !obrigatorio) return null;

  const enviarFolha = async (f) => {
    const key = `${Date.now()}-${Math.random()}`;
    const url = URL.createObjectURL(f);
    setFolhas((p) => [...p, { key, url, status: "enviando" }]);
    try {
      const foto = await prepararFoto(f, { minQ: 0.7, alvoKB: 600 });
      const fd = new FormData();
      fd.append("file", foto); fd.append("servico_id", servicoId); fd.append("categoria", "manual");
      await api.post("/upload", fd);
      setFolhas((p) => p.map((x) => x.key === key ? { ...x, status: "ok" } : x));
    } catch (e) {
      setFolhas((p) => p.filter((x) => x.key !== key));
      toast.error(e?.userMessage || formatApiError(e));
    }
  };

  const onCamera = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    enviarFolha(f);
    // Captura em sequência: reabre a câmera imediatamente p/ a próxima folha
    try { camRef.current?.click(); } catch { /* navegador pode exigir novo toque */ }
  };

  const onGaleria = (e) => {
    const arr = Array.from(e.target.files || []);
    e.target.value = "";
    arr.forEach(enviarFolha);
  };

  const enviadas = folhas.filter((x) => x.status === "ok").length;
  const enviando = folhas.some((x) => x.status === "enviando");

  const concluir = async () => {
    setBusy(true);
    try {
      await api.post(`/servicos/${servicoId}/manual-concluido`);
      toast.success("Manual registrado. Obrigado!");
      load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="border-2 border-[#FF6700] bg-white p-4 mt-4" data-testid="manual-produto">
      <div className="font-heading font-bold text-sm">📖 Manual do Produto {st?.manual_enviado ? "— enviado ✅" : "(OBRIGATÓRIO)"}</div>
      {!st?.manual_enviado && (
        <>
          <p className="text-xs text-muted-foreground mt-1">
            Fotografe TODAS as folhas do manual, do início ao fim, de forma legível.
            Este produto ainda precisa de manual ({st?.coletas ?? 0}/5 coletas na plataforma).
          </p>
          <div className="mt-3 space-y-2">
            <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden"
              data-testid="manual-foto-input" onChange={onCamera} />
            <input ref={galRef} type="file" accept="image/*" multiple className="hidden"
              data-testid="manual-galeria-input" onChange={onGaleria} />
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant="outline" onClick={() => camRef.current?.click()}
                className="btn-sharp border-[#FF6700] text-[#C2410C] hover:bg-orange-50 whitespace-normal h-auto py-2"
                data-testid="manual-camera-btn">
                📷 {enviadas > 0 ? "Fotografar próxima folha" : "Fotografar em sequência"}
              </Button>
              <Button type="button" variant="outline" onClick={() => galRef.current?.click()}
                className="btn-sharp whitespace-normal h-auto py-2" data-testid="manual-galeria-btn">
                🖼️ Escolher da galeria (várias)
              </Button>
            </div>
            {folhas.length > 0 && (
              <div className="grid grid-cols-4 gap-2 pt-1" data-testid="manual-miniaturas">
                {folhas.map((x, i) => (
                  <div key={x.key} className="relative border border-border" data-testid={`manual-folha-${i}`}>
                    <img src={x.url} alt={`Folha ${i + 1}`} className="w-full h-20 object-cover" />
                    <span className="absolute top-0 left-0 bg-black/60 text-white text-[10px] font-bold px-1.5 py-0.5">{i + 1}</span>
                    <span className={`absolute bottom-0 right-0 text-[10px] font-bold px-1.5 py-0.5 ${x.status === "ok" ? "bg-[#00A859] text-white" : "bg-[#FFCC00] text-black"}`}>
                      {x.status === "ok" ? "✓" : "…"}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className="text-xs text-muted-foreground">
              {enviadas + jaEnviadas} folha(s) enviada(s){enviando ? " — enviando…" : ""}
              {jaEnviadas > 0 && enviadas === 0 && <span className="font-bold text-[#00784A]"> — {jaEnviadas} já registrada(s), não precisa reenviar</span>}
            </div>
            <Button onClick={concluir} disabled={busy || (enviadas + jaEnviadas) === 0 || enviando}
              className="btn-sharp brand-bg text-white w-full" data-testid="manual-concluir-btn">
              {busy ? "Registrando…" : "Concluir envio do manual"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function FileSlot({ label, categoria, accept = "image/*", testid, onUpload, ev }) {
  const isVideo = accept.startsWith("video");
  return (
    <div className="border border-dashed border-border p-3 space-y-2.5" data-testid={`${testid}-slot`}>
      <div className="flex items-start gap-3">
        {ev && (
          <img src={fileUrl(ev)} alt={label} className="w-14 h-14 object-cover border border-border shrink-0" data-testid={`${testid}-thumb`} />
        )}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold leading-snug break-words" data-testid={`${testid}-label`}>{label}</div>
          {ev ? (
            <div className="text-[11px] font-bold text-[#00784A] mt-0.5" data-testid={`${testid}-ok`}>
              ✓ já enviada — não precisa reenviar
            </div>
          ) : (
            <div className="text-[11px] text-muted-foreground mt-0.5">{isVideo ? "Grave ou envie o vídeo" : "Toque para fotografar ou enviar"}</div>
          )}
        </div>
      </div>
      <label className="btn-sharp brand-bg text-white text-xs font-bold inline-flex items-center justify-center w-full sm:w-auto px-4 py-2.5 cursor-pointer select-none">
        {ev ? (isVideo ? "Substituir vídeo" : "Substituir foto") : (isVideo ? "Enviar vídeo" : "Enviar foto")}
        <input type="file" accept={accept} capture="environment" data-testid={testid}
          onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0], categoria)}
          className="sr-only" />
      </label>
    </div>
  );
}
